import { z } from "zod";

export interface InviteForm {
	mode: "email" | "managed";
	invites: {
		email?: string;
		userName?: string;
		name?: string;
		role: "member" | "admin";
	}[];
}

/**
 * Row schema for Email mode.
 */
export function emailInviteRowSchema(
	t: (key: string, params?: Record<string, unknown>) => string,
) {
	return z.object({
		email: z
			.string()
			.trim()
			.min(1, t("user-invite.email-required"))
			.pipe(z.email({ message: t("user-invite.email-invalid") })),
		userName: z.string().optional(),
		name: z.string().optional(),
		role: z.enum(["member", "admin"]),
	});
}

/**
 * Row schema for Managed mode.
 */
export function managedInviteRowSchema(
	t: (key: string, params?: Record<string, unknown>) => string,
) {
	return z.object({
		email: z.string().optional(),
		userName: z
			.string()
			.trim()
			.min(1, t("user-invite.username-required"))
			.min(4, t("user-invite.username-too-short"))
			.max(255, t("user-invite.username-too-long"))
			.refine(
				(v) => !/\s/.test(v),
				t("user-invite.username-invalid-spaces"),
			),
		name: z.string().optional(),
		role: z.enum(["member", "admin"]),
	});
}

/**
 * Form schema (discriminated by mode) + simple max count check.
 * Note: max count is dynamic so we leave that in a small superRefine.
 */
export function makeInviteFormSchema(
	t: (key: string, params?: Record<string, unknown>) => string,
	opts: { getMax: () => number },
) {
	const EmailForm = z.object({
		mode: z.literal("email"),
		invites: z.array(emailInviteRowSchema(t)),
	});

	const ManagedForm = z.object({
		mode: z.literal("managed"),
		invites: z.array(managedInviteRowSchema(t)),
	});

	const Form = z.discriminatedUnion("mode", [EmailForm, ManagedForm]);

	return Form.superRefine((data, ctx) => {
		const max = opts.getMax();
		if (data.invites.length > max) {
			ctx.addIssue({
				code: "custom",
				path: ["invites"],
				message: t("user-invite.too-many", { count: max }),
			});
		}
	});
}
